import { Icon } from './Icon';

/**
 * Every clickable thing in the app.
 *
 * Variants exist so a page never invents its own button styling. `primary` is
 * brand blue; `accent` is orange and is deliberately scarce — it marks the one
 * action a screen is really about, and loses its meaning the moment a screen
 * has two of them.
 */

const VARIANTS = {
  primary:
    'bg-brand text-on-brand border-transparent hover:bg-brand-hover active:translate-y-px',
  accent:
    'bg-accent text-white border-transparent hover:bg-accent-hover active:translate-y-px',
  secondary:
    'bg-surface text-ink border-line hover:bg-surface-2 hover:border-line-strong',
  ghost:
    'bg-transparent text-ink-2 border-transparent hover:bg-surface-2 hover:text-ink',
  danger:
    'bg-critical text-white border-transparent hover:opacity-90 active:translate-y-px',
  link: 'bg-transparent text-brand border-transparent hover:underline underline-offset-4 px-0',
};

const SIZES = {
  sm: 'h-8 px-3 text-[13px] gap-1.5 rounded-lg',
  md: 'h-9.5 px-3.5 text-sm gap-2 rounded-lg',
  lg: 'h-11 px-5 text-[15px] gap-2 rounded-xl',
  icon: 'h-9 w-9 justify-center rounded-lg',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconRight,
  loading = false,
  disabled = false,
  className = '',
  children,
  ...rest
}) {
  const isDisabled = disabled || loading;

  return (
    <button
      type="button"
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center border font-medium transition-colors select-none',
        'disabled:opacity-45 disabled:pointer-events-none',
        SIZES[size],
        VARIANTS[variant],
        className,
      ].join(' ')}
      {...rest}
    >
      {loading ? (
        <span
          className="h-3.5 w-3.5 rounded-full border-2 border-current border-r-transparent animate-spin"
          aria-hidden="true"
        />
      ) : (
        icon && <Icon name={icon} size={size === 'lg' ? 'md' : 'sm'} />
      )}
      {children}
      {iconRight && <Icon name={iconRight} size="sm" />}
    </button>
  );
}

/** A row of mutually exclusive options — used for ranges, filters and views. */
export function SegmentedControl({ value, onChange, options, size = 'sm', className = '' }) {
  return (
    <div
      role="tablist"
      className={`inline-flex items-center gap-0.5 rounded-lg border border-line bg-surface-2 p-0.5 ${className}`}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={[
              'rounded-md font-medium transition-colors',
              size === 'sm' ? 'px-2.5 py-1 text-[12.5px]' : 'px-3 py-1.5 text-sm',
              active
                ? 'bg-surface text-ink shadow-xs'
                : 'text-ink-3 hover:text-ink-2',
            ].join(' ')}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
